import axios from 'axios';
import { REST_HOSTNAME } from '../Config';

const axiosInstance = axios.create({
    baseURL: REST_HOSTNAME
});

// Add request interceptor
axiosInstance.interceptors.request.use(
    function (config) {
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            config.headers['x-access-token'] = jwt; // Add JWT to the headers
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);

export interface IChat {
    id: number;
    language_level: LanguageLevelEnum;
    first_line: string;
    latest_chat_date?: string;
}

export interface IChatLine {
    id: number;
    chat_id: number;
    role: string;
    line: string;
    pinyin: string[];
}

export interface IHskDetail {
    hsk_level: number;
    word: string;
    pinyin: string;
    definition: string;
}

export enum LanguageLevelEnum {
    Hsk1 = "HSK_1",
    Hsk2 = "HSK_2",
    Hsk3 = "HSK_3",
    Hsk4 = "HSK_4",
    Hsk5 = "HSK_5",
    Hsk6 = "HSK_6",
    ActflNoviceLow = "ACTFL_NOVICE_LOW",
    ActflNoviceMid = "ACTFL_NOVICE_MID",
    ActflNoviceHigh = "ACTFL_NOVICE_HIGH",
    ActflIntermediateLow = "ACTFL_INTERMEDIATE_LOW",
    ActflIntermediateMid = "ACTFL_INTERMEDIATE_MID",
    ActflIntermediateHigh = "ACTFL_INTERMEDIATE_HIGH",
    ActflAdvancedLow = "ACTFL_ADVANCED_LOW",
    ActflAdvancedMid = "ACTFL_ADVANCED_MID",
    ActflAdvancedHigh = "ACTFL_ADVANCED_HIGH",
    ActflSuperior = "ACTFL_SUPERIOR",
    ActflDistinguished = "ACTFL_DISTINGUISHED",
}

export const LanguageLevelLabels: Record<LanguageLevelEnum, string> = {
    [LanguageLevelEnum.Hsk1]: "HSK 1",
    [LanguageLevelEnum.Hsk2]: "HSK 2",
    [LanguageLevelEnum.Hsk3]: "HSK 3",
    [LanguageLevelEnum.Hsk4]: "HSK 4",
    [LanguageLevelEnum.Hsk5]: "HSK 5",
    [LanguageLevelEnum.Hsk6]: "HSK 6",
    [LanguageLevelEnum.ActflNoviceLow]: "ACTFL Novice Low",
    [LanguageLevelEnum.ActflNoviceMid]: "ACTFL Novice Mid",
    [LanguageLevelEnum.ActflNoviceHigh]: "ACTFL Novice High",
    [LanguageLevelEnum.ActflIntermediateLow]: "ACTFL Intermediate Low",
    [LanguageLevelEnum.ActflIntermediateMid]: "ACTFL Intermediate Mid",
    [LanguageLevelEnum.ActflIntermediateHigh]: "ACTFL Intermediate High",
    [LanguageLevelEnum.ActflAdvancedLow]: "ACTFL Advanced Low",
    [LanguageLevelEnum.ActflAdvancedMid]: "ACTFL Advanced Mid",
    [LanguageLevelEnum.ActflAdvancedHigh]: "ACTFL Advanced High",
    [LanguageLevelEnum.ActflSuperior]: "ACTFL Superior",
    [LanguageLevelEnum.ActflDistinguished]: "ACTFL Distinguished",
};

export const LanguageLevelShortLabels: Record<LanguageLevelEnum, string> = {
    [LanguageLevelEnum.Hsk1]: "H1",
    [LanguageLevelEnum.Hsk2]: "H2",
    [LanguageLevelEnum.Hsk3]: "H3",
    [LanguageLevelEnum.Hsk4]: "H4",
    [LanguageLevelEnum.Hsk5]: "H5",
    [LanguageLevelEnum.Hsk6]: "H6",
    [LanguageLevelEnum.ActflNoviceLow]: "A-NL",
    [LanguageLevelEnum.ActflNoviceMid]: "A-NM",
    [LanguageLevelEnum.ActflNoviceHigh]: "A-NH",
    [LanguageLevelEnum.ActflIntermediateLow]: "A-IL",
    [LanguageLevelEnum.ActflIntermediateMid]: "A-IM",
    [LanguageLevelEnum.ActflIntermediateHigh]: "A-IH",
    [LanguageLevelEnum.ActflAdvancedLow]: "A-AL",
    [LanguageLevelEnum.ActflAdvancedMid]: "A-AM",
    [LanguageLevelEnum.ActflAdvancedHigh]: "A-AH",
    [LanguageLevelEnum.ActflSuperior]: "A-S",
    [LanguageLevelEnum.ActflDistinguished]: "A-D",
};

export const ChatApi = {
    getAllConversationChat: function () {
        return axiosInstance.request<IChat[]>({
            method: "GET",
            url: '/conversation_chat'
        });
    },
    getConversationChatById: function (chatId: number) {
        return axiosInstance.request<IChat>({
            method: "GET",
            url: '/conversation_chat/' + chatId
        });
    },
    getAllOfficeHourChat: function () {
        return axiosInstance.request<IChat[]>({
            method: "GET",
            url: '/office_hour_chat'
        });
    },
    getOfficeHourChatById: function (chatId: number) {
        return axiosInstance.request<IChat>({
            method: "GET",
            url: '/office_hour_chat/' + chatId
        });
    },
    getChatLineByChat: function (chatId: number) {
        return axiosInstance.request<IChatLine[]>({
            method: "GET",
            url: '/chat_line?chat_id=' + chatId
        });
    },
    getChatLine: function (chatLineId: number) {
        return axiosInstance.request<IChatLine>({
            method: "GET",
            url: '/chat_line/' + chatLineId
        });
    },
    startNewConversationChatLine: function (chatLine: IChatLine, languageLevel: LanguageLevelEnum) {
        return axiosInstance.request<IChatLine[]>({
            method: "POST",
            url: '/conversation_chat/chat_line/' + languageLevel,
            data: chatLine
        });
    },
    startNewOfficeHourChatLine: function (chatLine: IChatLine, languageLevel: LanguageLevelEnum) {
        return axiosInstance.request<IChatLine[]>({
            method: "POST",
            url: '/office_hour_chat/chat_line/' + languageLevel,
            data: chatLine
        });
    },
    addConversationChatLine: function (chatLine: IChatLine, languageLevel: LanguageLevelEnum) {
        return axiosInstance.request<IChatLine[]>({
            method: "PUT",
            url: '/conversation_chat/chat_line?language_level=' + languageLevel,
            data: chatLine
        });
    },
    addOfficeHourChatLine: function (chatLine: IChatLine, languageLevel: LanguageLevelEnum) {
        return axiosInstance.request<IChatLine[]>({
            method: "PUT",
            url: '/office_hour_chat/chat_line?language_level=' + languageLevel,
            data: chatLine
        });
    },
    getHskDetailDetail: function (chatLineId: number) {
        return axiosInstance.request<IHskDetail[]>({
            method: "GET",
            url: '/hsk_detail/' + chatLineId
        });

    },
    deleteConversationChat: function (chatId: number) {
        return axiosInstance.request({
            method: "DELETE",
            url: '/conversation_chat/' + chatId
        });
    },
    deleteOfficeHourChat: function (chatId: number) {
        return axiosInstance.request({
            method: "DELETE",
            url: '/office_hour_chat/' + chatId
        });
    },

}
